import {
  FulfillmentTrackingDetails,
  FulfillmentTrackingStep
} from '../../../../redux/states/fulfillmentOrders/getFulfillmentOrderDetails/getFulfillmentOrderDetails.types'
import { GRAMS_PER_KILOGRAM } from '../../../../shared/dictionaries/unitConversion.dictionary'

/**
 * Compute the active step index from backend-provided trackingSteps and trackingDetails.
 *
 * MUI Stepper treats activeStep as the index of the CURRENT/NEXT step:
 * - Steps with index < activeStep are rendered as completed (checkmark)
 * - The step at activeStep is rendered as active (numbered circle)
 *
 * We return lastCompletedIndex + 1 so that out-of-order completions
 * (e.g. acceptedByUspsAt = true while withTraveler = false) are handled correctly —
 * the stepper advances to the furthest completed step rather than stalling at the
 * first gap.
 */
export const computeActiveStepIndex = (
  trackingSteps: FulfillmentTrackingStep[],
  trackingDetails: FulfillmentTrackingDetails
): number => {
  const visibleSteps = trackingSteps.filter((step) => step.visible)

  let lastCompletedIndex = -1

  for (let i = 0; i < visibleSteps.length; i++) {
    const stepKey = visibleSteps[i].key as keyof FulfillmentTrackingDetails
    const stepDetail = trackingDetails[stepKey]

    if (stepDetail?.status) {
      lastCompletedIndex = i
    }
  }

  return lastCompletedIndex + 1
}

/**
 * Returns true only when every visible step is complete (activeStepIndex === visibleSteps.length).
 * Used to tell the MUI Stepper to render the final step with a checkmark instead of a number.
 */
export const computeIsLastStepCompleted = (
  trackingSteps: FulfillmentTrackingStep[],
  trackingDetails: FulfillmentTrackingDetails
): boolean => {
  const visibleSteps = trackingSteps.filter((step) => step.visible)

  return computeActiveStepIndex(trackingSteps, trackingDetails) === visibleSteps.length
}

export const formatWeightDisplay = (weightGrams: number): string => {
  if (weightGrams >= GRAMS_PER_KILOGRAM) {
    return `${(weightGrams / GRAMS_PER_KILOGRAM).toFixed(1)}kg`
  }

  return `${weightGrams}g`
}

export const formatCentsToPrice = (cents: number, currency: string): string => {
  return `${currency} ${(cents / 100).toFixed(2)}`
}

export const buildStepperSteps = (trackingSteps: FulfillmentTrackingStep[]): { label: string }[] => {
  const visibleSteps = trackingSteps.filter((step) => step.visible)

  return visibleSteps.map((step) => ({
    label: step.subLabel ? `${step.label}\n(${step.subLabel})` : step.label
  }))
}
